import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { loadLocations, loadSectionTexts } from "../../utils/contentService";
import image1 from "../../assets/imgs/1.jpg";
import image2 from "../../assets/imgs/2.jpg";
import image3 from "../../assets/imgs/3.jpg";

// Fallback images if Firestore URLs are not available
const fallbackImages = [image1, image2, image3];

const InfoDetailModal = ({ isOpen, onClose, location, locationIndex, texts }) => {
  if (!isOpen || !location) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-8 flex flex-col gap-6 animate-fade-in shadow-blue/20 border border-blue1/10">
        <div className="flex justify-between items-center border-b border-gray-100 pb-4">
          <h2 className="text-2xl md:text-3xl font-serif italic font-medium text-black text-center w-full">
            {location.title}
          </h2>
        </div>

        <div className="flex flex-col gap-6 py-4">
          {/* Description - Centered and Styled */}
          <div className="space-y-8 text-center">
            {location.details ? (
              <div className="text-base md:text-lg font-serif italic text-gray-800 leading-relaxed whitespace-pre-line px-2 md:px-12">
                {/* Check if content has the section divider '---' */}
                {location.details.split(/\s*---\s*/).map((section, idx) => (
                  <div key={idx}>
                    {idx > 0 && (
                      <div className="w-full h-px bg-black mx-auto my-8 md:my-12 opacity-40 md:opacity-100" />
                    )}
                    <div className="whitespace-pre-line text-black">
                      {section.trim()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-base md:text-lg font-serif italic text-gray-500 italic px-2 md:px-12">
                {location.description}
              </p>
            )}

            {/* PayPal Donate Button - only for the first card */}
            {locationIndex === 0 && (
              <div className="mt-8 flex justify-center">
                <a
                  href="https://sunnykids.info/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 px-5 py-3 border-0 rounded-xl font-semibold text-base cursor-pointer text-white shadow-lg transition-all duration-300 hover:brightness-110 hover:shadow-xl"
                  style={{ background: "#0070ba" }}
                >
                  {texts.foundationButton || ""}
                </a>
              </div>
            )}

            {/* Google Maps Button - if map_url is available */}
            {location.map_url && (
              <div className="mt-8 flex justify-center">
                <a
                  href={location.map_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 px-6 py-3 bg-blue1 hover:bg-blue text-white rounded-xl font-serif italic transition-all duration-300 shadow-md text-center no-underline"
                >
                  📍 {texts.mapButtonPiraeus || "Google Maps"}
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center pt-2">
          <button
            onClick={onClose}
            className="w-full md:w-auto px-10 py-3 bg-gray-900 hover:bg-black text-white rounded-xl font-serif italic transition-all duration-300 shadow-md self-center"
          >
            {texts.close || ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function LocationSection() {
  const { i18n } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [texts, setTexts] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const items = await loadLocations(i18n.language);
        // Add fallback images for local development
        const withImages = items.map((item, idx) => ({
          ...item,
          image:
            item.image_url && !item.image_url.startsWith("/static/media/")
              ? item.image_url
              : fallbackImages[idx] || image1,
        }));
        setLocations(withImages);
      } catch (error) {
        console.error("Failed to load locations:", error);
        // Fallback to placeholder data
        setLocations([
          { title: "TBA", description: "...", image: image1 },
          { title: "TBA", description: "...", image: image2 },
          { title: "TBA", description: "...", image: image3 },
        ]);
      }
    };

    fetchLocations();
    setTexts(loadSectionTexts("info", i18n.language));
  }, [i18n.language]);

  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleCardClick = (location, index) => {
    setSelectedLocation(location);
    setSelectedIndex(index);
    setIsModalOpen(true);
  };

  return (
    <section className="w-full py-12 md:py-16 lg:py-20 bg-test relative">
      <InfoDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        location={selectedLocation}
        locationIndex={selectedIndex}
        texts={texts}
      />

      <div className="mx-auto max-w-[1100px] px-4">
        <h2 className="text-[30px] lg:text-[45px] leading-tight lg:leading-[50px] italic font-medium font-serif text-center mb-8 lg:mb-16 text-black">
          {texts.title || ""}
        </h2>

        <div className="flex flex-col md:flex-row md:flex-wrap lg:flex-nowrap gap-8 md:gap-12 justify-center items-center">
          {locations.map((location, index) => (
            <div
              key={index}
              className="flex justify-center w-full min-[730px]:w-[450px] lg:w-[500px]"
            >
              <div
                onClick={() => handleCardClick(location, index)}
                className="w-full max-w-[320px] min-[730px]:max-w-none h-auto md:h-[470px] flex flex-col overflow-hidden rounded-sm shadow-sm bg-blue1 relative group cursor-pointer no-underline transition-all duration-300"
              >
                {/* Image */}
                <div className="w-full aspect-square md:h-[386px] md:aspect-auto shrink-0 overflow-hidden">
                  <img
                    src={location.image}
                    alt={location.title}
                    className="w-full h-full object-cover transition-all duration-300 group-hover:blur-[2px]"
                  />
                </div>

                {/* Text Content */}
                <div className="w-full flex-1 px-4 md:px-6 py-4 md:py-0 text-center md:text-left flex flex-col justify-center transition-all duration-300 group-hover:blur-[2px]">
                  <h3 className="text-lg md:text-xl font-normal font-serif italic text-white mb-1 leading-tight">
                    {location.title}
                  </h3>
                  <p className="text-sm md:text-base font-light font-serif italic text-white leading-tight">
                    {location.description}
                  </p>
                </div>

                {/* Hover Overlay with Text */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="px-6 py-3 bg-white text-black font-serif italic text-sm md:text-base rounded-lg">
                    {texts.mapButton || ""}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Travel Help Text */}
        {texts.travelHelp && (
          <div className="mt-12 md:mt-16 text-center max-w-4xl mx-auto ">
            <p className="text-[13px] lg:text-2xl font-light font-serif italic text-black leading-relaxed">
              {texts.travelHelp}{" "}
              <a
                href={`tel:+359895404889`}
                className="underline cursor-pointer hover:opacity-60 transition-opacity whitespace-normal lg:whitespace-nowrap"
              >
                {texts.travelHelpPhone}
              </a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
